// Per-project persistence of the TableView collapse/expand layout: which
// phases, sub-task containers, and non-phase buckets are folded away. Each
// roadmap remembers its own state so re-opening a project restores the layout
// the user last left it in. Mirrors the per-project localStorage convention
// used by grouping.ts (loadGroupBy/saveGroupBy).

const PREFIX = "juuplan:collapsed:";

/** Section kinds that carry their own collapse state in TableView. */
export type CollapseKind = "phases" | "tasks" | "buckets";

function keyFor(kind: CollapseKind, projectId: number): string {
  return `${PREFIX}${kind}:${projectId}`;
}

function read(kind: CollapseKind, projectId: number): string[] {
  if (typeof localStorage === "undefined") return [];
  const raw = localStorage.getItem(keyFor(kind, projectId));
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function write(kind: CollapseKind, projectId: number, values: string[]): void {
  if (typeof localStorage === "undefined") return;
  const key = keyFor(kind, projectId);
  // Empty = fully expanded, the default — drop the key so we don't accumulate
  // stale entries for every project the user has ever opened.
  if (values.length === 0) localStorage.removeItem(key);
  else localStorage.setItem(key, JSON.stringify(values));
}

/** Load a set of collapsed numeric ids (phase ids / task ids). */
export function loadCollapsedIds(
  kind: "phases" | "tasks",
  projectId: number
): Set<number> {
  const out = new Set<number>();
  for (const s of read(kind, projectId)) {
    const n = Number(s);
    if (!Number.isNaN(n)) out.add(n);
  }
  return out;
}

/** Persist a set of collapsed numeric ids. */
export function saveCollapsedIds(
  kind: "phases" | "tasks",
  projectId: number,
  ids: Set<number>
): void {
  write(kind, projectId, [...ids].map(String));
}

/** Load the set of collapsed bucket keys (stable strings). */
export function loadCollapsedBuckets(projectId: number): Set<string> {
  return new Set(read("buckets", projectId));
}

/** Persist the set of collapsed bucket keys. */
export function saveCollapsedBuckets(projectId: number, keys: Set<string>): void {
  write("buckets", projectId, [...keys]);
}
